import { pickCctvLivePlayback, resolveAbsoluteCctvStreamUrl } from '../../utils/cctvStreamUrl';
import {
  MONITORING_POPUP_BBOX_CSS,
  MONITORING_POPUP_BBOX_SCRIPT,
  MONITORING_POPUP_POOL_AI_SCRIPT,
  buildMonitoringPopupApiBootstrapScript,
  buildPopupBboxToolbarHtml,
} from './monitoringPopupBboxScript';
import { API_ORIGIN } from '../../config';
import { resolveSiteMonitorPlaybackFlags } from '../../utils/siteMonitorPlayback';
import { isRtspUrl, LOCAL_PREVIEW_ORIGIN } from '../../utils/streamPreviewSnapshot';

export type PoolMonitoringCctv = {
  id: number;
  pool_id?: number;
  name: string;
  location?: string;
  url?: string;
  stream_url?: string;
  playback_url?: string;
  webrtc_url?: string;
  status?: string;
  is_active?: boolean;
  camera_type?: string;
  purpose?: string;
};

/** 활성화 + 정상(ACTIVE)만 관제 화면에 표시 */
export function isCctvEligibleForMonitoring(cctv: {
  is_active?: boolean | null;
  status?: string | null;
}): boolean {
  if (cctv.is_active === false) return false;
  const status = String(cctv.status || 'ACTIVE').trim().toUpperCase();
  if (!status || status === 'ACTIVE' || status === 'ONLINE' || status === '정상') {
    return true;
  }
  return false;
}

/**
 * 통합/개별 관제에 올릴 CCTV 용도.
 * - 관제 모니터링 (등록 기본값)
 * - 관리 모니터링 (시설 관리 탭과 동일 계열, camera_type=management)
 * - 용도 미입력은 관제 기본으로 취급
 * 안전/기타는 제외.
 */
export function isCentralMonitoringCctvPurpose(cctv: {
  camera_type?: string | null;
  purpose?: string | null;
}): boolean {
  if (cctv.camera_type === 'management') return true;
  const raw = String(cctv.purpose ?? '').trim();
  if (!raw) return true;
  if (raw.includes('안전') || raw.toLowerCase().includes('safety')) return false;
  if (raw.includes('기타') || raw.toLowerCase().includes('other')) return false;
  if (raw.includes('관제')) return true;
  if (raw.includes('관리 모니터링') || raw === '관리 모니터링') return true;
  const lower = raw.toLowerCase();
  if (
    lower === 'monitoring' ||
    lower.includes('management monitoring') ||
    lower.includes('management')
  ) {
    return true;
  }
  // 영문 monitoring 만 있는 경우 (한글 '모니터링' 과 별개)
  if (lower.includes('monitoring')) return true;
  return false;
}

export type MonitoringPopupPool = {
  id?: string | number;
  code: string;
  name: string;
  status?: 'active' | 'inactive' | 'maintenance' | string;
  site_gateway_enabled?: boolean;
  site_monitor_prefer_local?: boolean;
};

export function filterManagementMonitoringCctvs(cctvs: PoolMonitoringCctv[]): PoolMonitoringCctv[] {
  return cctvs.filter(
    (cctv) => isCctvEligibleForMonitoring(cctv) && isCentralMonitoringCctvPurpose(cctv)
  );
}

export type PoolGridEntry = {
  pool: MonitoringPopupPool;
  cctvs: PoolMonitoringCctv[];
  grid: (PoolMonitoringCctv | null)[][];
  maxRow: number;
  maxCol: number;
};

export function buildPoolGridEntry(
  pool: MonitoringPopupPool,
  cctvs: PoolMonitoringCctv[],
  options?: { skipPurposeFilter?: boolean }
): PoolGridEntry {
  const safetyCctvs = options?.skipPurposeFilter
    ? cctvs
    : filterManagementMonitoringCctvs(cctvs);

  let maxRow = 0;
  let maxCol = 0;

  safetyCctvs.forEach((cctv) => {
    if (!cctv.location) return;
    const [row, col] = cctv.location.split('-').map(Number);
    if (!isNaN(row) && !isNaN(col)) {
      maxRow = Math.max(maxRow, row);
      maxCol = Math.max(maxCol, col);
    }
  });

  const grid: (PoolMonitoringCctv | null)[][] = [];
  for (let i = 0; i <= maxRow; i++) {
    grid[i] = [];
    for (let j = 0; j <= maxCol; j++) {
      grid[i][j] = null;
    }
  }

  safetyCctvs.forEach((cctv) => {
    if (!cctv.location) return;
    const [row, col] = cctv.location.split('-').map(Number);
    if (row > 0 && col > 0 && row <= maxRow && col <= maxCol) {
      grid[row][col] = cctv;
    }
  });

  return { pool, cctvs: safetyCctvs, grid, maxRow, maxCol };
}

const LOCATION_SPLIT_RE = /(\d+|\D+)/g;

export function sortCctvsByLocation(cctvs: PoolMonitoringCctv[]): PoolMonitoringCctv[] {
  return [...cctvs].sort((a, b) => {
    const la = String(a.location || a.name || '');
    const lb = String(b.location || b.name || '');
    const pa = la.match(LOCATION_SPLIT_RE) || [la];
    const pb = lb.match(LOCATION_SPLIT_RE) || [lb];
    const n = Math.max(pa.length, pb.length);
    for (let i = 0; i < n; i++) {
      const xa = pa[i] ?? '';
      const xb = pb[i] ?? '';
      const na = Number(xa);
      const nb = Number(xb);
      const bothNum =
        Number.isFinite(na) && Number.isFinite(nb) && xa.trim() !== '' && xb.trim() !== '';
      if (bothNum) {
        if (na !== nb) return na - nb;
        continue;
      }
      const cmp = xa.localeCompare(xb, 'ko');
      if (cmp !== 0) return cmp;
    }
    return a.id - b.id;
  });
}

export type RotationSlide = {
  url: string;
  name: string;
  location: string;
  cctvId: number;
  webrtcUrl: string;
};

export function buildRotationSlides(
  entry: PoolGridEntry,
  useRemotePlayback: boolean,
  options?: { applySiteLocal?: boolean }
): RotationSlide[] {
  const flags = resolveSiteMonitorPlaybackFlags({
    useRemotePlayback,
    siteGatewayEnabled: Boolean(entry.pool.site_gateway_enabled),
    siteMonitorPreferLocal: Boolean(entry.pool.site_monitor_prefer_local),
    applySiteLocal: Boolean(options?.applySiteLocal),
  });
  return sortCctvsByLocation(entry.cctvs).flatMap((cctv) => {
    const rawStream = (cctv.stream_url || cctv.url || '').trim();
    if (flags.preferLocalRtspSnapshot && isRtspUrl(rawStream)) {
      const name = (cctv.name || '').trim() || `CCTV ${cctv.id}`;
      const location = (cctv.location || '').trim();
      return [
        {
          url: rawStream,
          webrtcUrl: '',
          name,
          location,
          cctvId: cctv.id,
        },
      ];
    }
    const live = pickCctvLivePlayback(
      cctv,
      flags.useRemotePlayback,
      flags.siteGatewayEnabled
    );
    const url =
      live.hlsUrl ||
      resolveAbsoluteCctvStreamUrl(
        cctv,
        flags.useRemotePlayback,
        flags.siteGatewayEnabled
      );
    if (!url && !live.webrtcUrl) return [];
    const name = (cctv.name || '').trim() || `CCTV ${cctv.id}`;
    const location = (cctv.location || '').trim();
    return [
      {
        url: url || '',
        webrtcUrl: live.webrtcUrl || '',
        name,
        location,
        cctvId: cctv.id,
      },
    ];
  });
}

function escapeScriptJson(value: unknown): string {
  return JSON.stringify(value).replace(/</g, '\\u003c');
}

function buildRotationOpenOnclick(poolCode: string, rotationIntervalSec: number): string {
  return (
    'openPoolRotationPopup(\'' +
    String(poolCode).replace(/\\/g, '\\\\').replace(/'/g, "\\'") +
    '\',' +
    rotationIntervalSec +
    ',event); return false;'
  );
}

function buildRotationOpenAtCctvOnclick(
  poolCode: string,
  rotationIntervalSec: number,
  cctvId: number
): string {
  return (
    'openPoolRotationPopupAtCctv(\'' +
    String(poolCode).replace(/\\/g, '\\\\').replace(/'/g, "\\'") +
    '\',' +
    rotationIntervalSec +
    ',' +
    cctvId +
    ',event); return false;'
  );
}

/** 그리드/전체 모니터링 팝업 HTML에 포함 — 클릭 창에서 순환 창을 동기 open */
export const MONITORING_POPUP_ROTATION_OPEN_SCRIPT =
  'function getMonitoringPopupOpener() {' +
  'var root = window.top || window;' +
  'try { if (root.opener && !root.opener.closed) return root.opener; } catch (e) {}' +
  'try { if (window.parent && window.parent !== window && window.parent.opener && !window.parent.opener.closed) return window.parent.opener; } catch (e2) {}' +
  'return null;' +
  '}' +
  'function openPoolFullscreenPopup(poolId, poolCode, ev) {' +
  'if (ev) { ev.stopPropagation(); ev.preventDefault(); }' +
  'var root = window.top || window;' +
  'var op = getMonitoringPopupOpener();' +
  'var code = String(poolCode || "").trim();' +
  'if (!code) { alert("전체화면을 여기 불가."); return false; }' +
  'var base = root.location.origin || (op ? op.location.origin : "");' +
  'if (!base) { alert("전체화면을 여기 불가."); return false; }' +
  'var codes = [];' +
  'try {' +
  '  var sections = document.querySelectorAll(".pool-section[data-pool-code]");' +
  '  for (var si = 0; si < sections.length; si++) {' +
  '    var pc = String(sections[si].getAttribute("data-pool-code") || "").trim();' +
  '    if (pc && codes.indexOf(pc) < 0) codes.push(pc);' +
  '  }' +
  '} catch (eCodes) {}' +
  'var qsParts = [];' +
  'if (codes.length > 0) {' +
  '  qsParts.push("poolCodes=" + encodeURIComponent(codes.join(",")));' +
  '}' +
  'try {' +
  '  var poolSec = parseInt(localStorage.getItem("monitoringPoolGridRotationIntervalSec") || "", 10);' +
  '  if (!Number.isFinite(poolSec)) poolSec = parseInt(localStorage.getItem("monitoringRotationIntervalSec") || "5", 10);' +
  '  if (Number.isFinite(poolSec) && poolSec >= 1) {' +
  '    if (poolSec > 300) poolSec = 300;' +
  '    qsParts.push("interval=" + Math.floor(poolSec));' +
  '  }' +
  '} catch (eSec) {}' +
  'qsParts.push("inplace=1");' +
  'try {' +
  '  var ret = String((root.location.pathname || "") + (root.location.search || ""));' +
  '  if (ret.indexOf("/") === 0) qsParts.push("returnTo=" + encodeURIComponent(ret));' +
  '} catch (eRet) {}' +
  'var qs = qsParts.length ? ("?" + qsParts.join("&")) : "";' +
  'var url = base + "/" + encodeURIComponent(code) + "/grid" + qs;' +
  // 동일 창에서 표시 (새 창·opener 이중 스트림 부하 방지)
  'try { root.location.assign(url); } catch (eNav) { root.location.href = url; }' +
  'return false;' +
  '}' +
  'function openPoolRotationPopup(poolCode, intervalSec, ev) {' +
  'if (ev) { ev.stopPropagation(); ev.preventDefault(); }' +
  'var root = window.top || window;' +
  'var op = getMonitoringPopupOpener();' +
  'var code = String(poolCode || "").trim();' +
  'if (!code) { alert("순환 보기를 여기 불가."); return false; }' +
  'var base = root.location.origin || (op ? op.location.origin : "");' +
  'if (!base) { alert("순환 보기를 여기 불가."); return false; }' +
  'var sec = Math.floor(Number(intervalSec));' +
  'if (!Number.isFinite(sec) || sec < 1) sec = 5;' +
  'if (sec > 300) sec = 300;' +
  'var qsParts = ["interval=" + sec, "inplace=1"];' +
  'try {' +
  '  var ret = String((root.location.pathname || "") + (root.location.search || ""));' +
  '  if (ret.indexOf("/") === 0) qsParts.push("returnTo=" + encodeURIComponent(ret));' +
  '} catch (eRet) {}' +
  'var url = base + "/" + encodeURIComponent(code) + "/rotation?" + qsParts.join("&");' +
  'try { root.location.assign(url); } catch (eNav) { root.location.href = url; }' +
  'return false;' +
  '}' +
  'function openPoolRotationPopupAtCctv(poolCode, intervalSec, cctvId, ev) {' +
  'if (ev) { ev.stopPropagation(); ev.preventDefault(); }' +
  'var root = window.top || window;' +
  'var op = getMonitoringPopupOpener();' +
  'var code = String(poolCode || "").trim();' +
  'if (!code) { alert("순환 보기를 여기 불가."); return false; }' +
  'var base = root.location.origin || (op ? op.location.origin : "");' +
  'if (!base) { alert("순환 보기를 여기 불가."); return false; }' +
  'var sec = Math.floor(Number(intervalSec));' +
  'if (!Number.isFinite(sec) || sec < 1) sec = 5;' +
  'if (sec > 300) sec = 300;' +
  'var cid = Math.floor(Number(cctvId));' +
  'if (!Number.isFinite(cid)) { return openPoolRotationPopup(poolCode, sec, ev); }' +
  'var qsParts = ["interval=" + sec, "cctvId=" + encodeURIComponent(String(cid)), "paused=1", "inplace=1"];' +
  'try {' +
  '  var ret2 = String((root.location.pathname || "") + (root.location.search || ""));' +
  '  if (ret2.indexOf("/") === 0) qsParts.push("returnTo=" + encodeURIComponent(ret2));' +
  '} catch (eRet2) {}' +
  'var url = base + "/" + encodeURIComponent(code) + "/rotation?" + qsParts.join("&");' +
  'try { root.location.assign(url); } catch (eNav2) { root.location.href = url; }' +
  'return false;' +
  '}' +
  'window.getMonitoringPopupOpener = getMonitoringPopupOpener;' +
  'window.openPoolFullscreenPopup = openPoolFullscreenPopup;' +
  'window.openPoolRotationPopup = openPoolRotationPopup;' +
  'window.openPoolRotationPopupAtCctv = openPoolRotationPopupAtCctv;';

/** srcDoc iframe 안에서도 실제 팝업(최상위 창)을 닫기 */
export const MONITORING_POPUP_CLOSE_HANDLER_SCRIPT =
  'function closeMonitoringPopupWindow(ev) {' +
  'if (ev) { ev.stopPropagation(); ev.preventDefault(); }' +
  'var topWin = window.top || window;' +
  'try { topWin.close(); } catch (e) {}' +
  'if (topWin && !topWin.closed && window.parent && window.parent !== window) {' +
  '  try { window.parent.postMessage({ type: "cctv-monitoring-close" }, "*"); } catch (e2) {}' +
  '}' +
  'return false;' +
  '}';

/** 전체 관제 새 창: 배경 흰색/검정 토글 (DOMContentLoaded 내부에서 실행) */
export const MONITORING_POPUP_BG_THEME_SCRIPT =
  'var BG_THEME_KEY = "centralAllMonitoringBgLight";' +
  'function syncBgThemeUi(light) {' +
  '  document.documentElement.classList.toggle("monitoring-bg-light", light);' +
  '  var iconLight = document.getElementById("bg-theme-icon-light");' +
  '  var iconDark = document.getElementById("bg-theme-icon-dark");' +
  '  var bgBtn = document.getElementById("bg-theme-toggle");' +
  '  if (iconLight) iconLight.style.display = light ? "none" : "block";' +
  '  if (iconDark) iconDark.style.display = light ? "block" : "none";' +
  '  if (bgBtn) bgBtn.title = light ? "\\uc5b4\\ub450\\uc6b4 \\ubc30\\uacbd" : "\\ubc1d\\uc740 \\ubc30\\uacbd";' +
  '  try { window.parent.postMessage({ type: "cctv-monitoring-bg", light: light }, "*"); } catch (eBg) {}' +
  '}' +
  'var initialBgLight = false;' +
  'try { initialBgLight = localStorage.getItem(BG_THEME_KEY) === "1"; } catch (eBg2) {}' +
  'syncBgThemeUi(initialBgLight);' +
  'var bgThemeToggle = document.getElementById("bg-theme-toggle");' +
  'if (bgThemeToggle) {' +
  '  bgThemeToggle.addEventListener("click", function(e) {' +
  '    e.stopPropagation();' +
  '    var nextLight = !document.documentElement.classList.contains("monitoring-bg-light");' +
  '    syncBgThemeUi(nextLight);' +
  '    try { localStorage.setItem(BG_THEME_KEY, nextLight ? "1" : "0"); } catch (eBg3) {}' +
  '  });' +
  '}';

const MONITORING_POPUP_BG_THEME_CSS =
  '.bg-theme-button { position: fixed; bottom: 20px; right: 44px; width: 24px; height: 24px; background: rgba(0,0,0,0.5); color: white; border: none; border-radius: 50%; cursor: pointer; display: flex; align-items: center; justify-content: center; z-index: 200; box-shadow: 0 1px 3px rgba(0,0,0,0.2); }' +
  '.bg-theme-button:hover { background: rgba(0,0,0,0.8); }' +
  'html.monitoring-bg-light body, html.monitoring-bg-light .main-grid, html.monitoring-bg-light .pool-section, html.monitoring-bg-light .grid-container { background-color: #fff; color: #111; }' +
  'html.monitoring-bg-light .cctv-cell, html.monitoring-bg-light video { background-color: #fff; }' +
  'html.monitoring-bg-light .bg-theme-button, html.monitoring-bg-light .fullscreen-button { background: rgba(0,0,0,0.35); }';

const POPUP_CLOSE_ONCLICK = 'closeMonitoringPopupWindow(event); return false;';

const POPUP_TOOLBAR_CSS =
  '.popup-toolbar { position: fixed; top: 10px; right: 10px; z-index: 400; display: flex; flex-wrap: wrap; gap: 8px; justify-content: flex-end; max-width: 90vw; }' +
  '.popup-btn { background: rgba(34,34,34,0.92); color: #fff; border: 1px solid #666; border-radius: 4px; padding: 6px 12px; font-size: 12px; cursor: pointer; font-family: Arial, sans-serif; }' +
  '.popup-btn:hover { background: rgba(60,60,60,0.95); }' +
  '.popup-btn-close { border-color: #b71c1c; }' +
  '.pool-toolbar { display: flex; align-items: center; gap: 6px; padding: 4px 8px; background: #1a1a1a; border-bottom: 1px solid #444; flex-shrink: 0; }' +
  '.pool-toolbar .popup-btn { padding: 3px 8px; font-size: 11px; }' +
  '.pool-ai-toggle { display: inline-flex; align-items: center; gap: 4px; color: #eee; font-size: 11px; cursor: pointer; user-select: none; margin-right: 4px; }' +
  '.pool-ai-toggle input { cursor: pointer; }' +
  '.popup-icon-btn { display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; padding: 0; background: rgba(34,34,34,0.92); color: #90caf9; border: 1px solid #555; border-radius: 4px; cursor: pointer; }' +
  '.popup-icon-btn:hover { background: rgba(60,60,60,0.95); border-color: #90caf9; }' +
  '.popup-icon-btn svg { display: block; }';

function buildPopupToolbarHtml(options: {
  showClose?: boolean;
  showRotation?: boolean;
  poolCode?: string;
  rotationIntervalSec?: number;
  /** 닫기 버튼 문구 (inplace 전체화면: 돌아가기) */
  closeLabel?: string;
}): string {
  const {
    showClose = true,
    showRotation,
    poolCode,
    rotationIntervalSec = 5,
    closeLabel = '창 닫기',
  } = options;
  let html = '<div class="popup-toolbar">';
  html += buildPopupBboxToolbarHtml();
  if (showRotation && poolCode) {
    html +=
      '<button type="button" class="popup-btn" onclick="' +
      buildRotationOpenOnclick(poolCode, rotationIntervalSec) +
      '">순환 보기</button>';
  }
  if (showClose) {
    html +=
      '<button type="button" class="popup-btn popup-btn-close" onclick="' +
      POPUP_CLOSE_ONCLICK +
      '">' +
      closeLabel +
      '</button>';
  }
  html += '</div>';
  return html;
}

function buildPoolActionToolbarHtml(
  pool: MonitoringPopupPool,
  options: { enableActions: boolean; rotationIntervalSec: number }
): string {
  if (!options.enableActions || pool.id == null || !pool.code) return '';
  const poolId = String(pool.id).replace(/"/g, '');
  const poolName = String(pool.name || '')
    .replace(/\\/g, '\\\\')
    .replace(/'/g, "\\'")
    .replace(/"/g, '&quot;');
  const rotOnclick = buildRotationOpenOnclick(String(pool.code), options.rotationIntervalSec);
  const fullscreenOnclick =
    'event.stopPropagation();return openPoolFullscreenPopup(' +
    JSON.stringify(String(pool.id)) +
    ',' +
    JSON.stringify(String(pool.code)) +
    ',event);';
  const alertOnclick =
    "event.stopPropagation();" +
    "try{window.parent.postMessage({type:'monitoring-popup-manual-alert',poolId:" +
    Number(pool.id) +
    ",poolName:'" +
    poolName +
    "'},'*');}catch(e){}return false;";

  const bellSvg =
    '<svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/></svg>';
  const camSvg =
    '<svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"/></svg>';
  const slideSvg =
    '<svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M10 8v8l5-4-5-4zm9-5H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14z"/></svg>';

  return (
    '<div class="pool-toolbar" onclick="event.stopPropagation();">' +
    '<label class="pool-ai-toggle" title="AI 탐지 ON/OFF">' +
    '<input type="checkbox" class="pool-ai-detect" data-pool-id="' +
    poolId +
    '" />' +
    'AI 탐지</label>' +
    '<button type="button" class="popup-icon-btn" title="알림 메시지 전송" onclick="' +
    alertOnclick +
    '">' +
    bellSvg +
    '</button>' +
    '<button type="button" class="popup-icon-btn" title="전체화면" onclick="' +
    fullscreenOnclick +
    '">' +
    camSvg +
    '</button>' +
    '<button type="button" class="popup-icon-btn" title="순환 보기" onclick="event.stopPropagation(); ' +
    rotOnclick +
    '">' +
    slideSvg +
    '</button>' +
    '</div>'
  );
}

function buildPoolSectionHtml(
  entry: PoolGridEntry,
  useRemotePlayback: boolean,
  enablePoolHeaderClick: boolean,
  sectionOptions?: {
    enableRotationButton?: boolean;
    rotationIntervalSec?: number;
    /** false면 CCTV 셀 클릭으로 순환 보기 열지 않음 (시설 전체화면 롤링용) */
    enableCctvClickRotation?: boolean;
    applySiteLocal?: boolean;
  }
): string {
  const { pool, grid, maxRow, maxCol } = entry;
  const flags = resolveSiteMonitorPlaybackFlags({
    useRemotePlayback,
    siteGatewayEnabled: Boolean(pool.site_gateway_enabled),
    siteMonitorPreferLocal: Boolean(pool.site_monitor_prefer_local),
    applySiteLocal: Boolean(sectionOptions?.applySiteLocal),
  });
  const rotSec = sectionOptions?.rotationIntervalSec ?? 5;
  const poolCodeForRotation = pool.code ? String(pool.code) : '';
  const enableCctvClickRotation = sectionOptions?.enableCctvClickRotation !== false;

  let cellsHtml = '';
  if (maxRow > 0 && maxCol > 0) {
    for (let row = 1; row <= maxRow; row++) {
      for (let col = 1; col <= maxCol; col++) {
        const cctv = grid[row] && grid[row][col];
        if (!cctv) continue;
        const rawStream = (cctv.stream_url || cctv.url || '').trim();
        const useRtspSnap =
          flags.preferLocalRtspSnapshot && isRtspUrl(rawStream);
        const live = useRtspSnap
          ? { mode: 'none' as const, webrtcUrl: '', hlsUrl: '' }
          : pickCctvLivePlayback(
              cctv,
              flags.useRemotePlayback,
              flags.siteGatewayEnabled
            );
        const streamUrl = useRtspSnap
          ? rawStream
          : live.hlsUrl ||
            resolveAbsoluteCctvStreamUrl(
              cctv,
              flags.useRemotePlayback,
              flags.siteGatewayEnabled
            );
        if (!streamUrl && !live.webrtcUrl) continue;
        const poolIdNum = Number(pool.id);
        const poolIdAttr =
          pool.id != null && Number.isFinite(poolIdNum) ? String(poolIdNum) : '';
        const cellClick =
          enableCctvClickRotation && poolCodeForRotation
            ? ' onclick="' +
              buildRotationOpenAtCctvOnclick(poolCodeForRotation, rotSec, cctv.id) +
              '" title="클릭: 순환 보기(일시정지)"'
            : '';
        const clickableClass =
          enableCctvClickRotation && poolCodeForRotation ? ' cctv-cell-clickable' : '';
        const webrtcAttr =
          !useRtspSnap && live.webrtcUrl
            ? '" data-webrtc-url="' + live.webrtcUrl.replace(/"/g, '&quot;')
            : '';
        const rtspAttr = useRtspSnap ? '" data-rtsp-snapshot="1' : '';
        cellsHtml +=
          '<div class="cctv-cell' +
          clickableClass +
          '" data-pool-id="' +
          poolIdAttr +
          '" data-cctv-id="' +
          cctv.id +
          '"' +
          cellClick +
          ' style="grid-row: ' +
          row +
          '; grid-column: ' +
          col +
          ';">' +
          (useRtspSnap
            ? '<img id="cctv-' +
              cctv.id +
              '" alt="" data-stream-url="' +
              streamUrl.replace(/"/g, '&quot;') +
              rtspAttr +
              '" style="width:100%;height:100%;object-fit:cover;background:#000;" />'
            : '<video id="cctv-' +
              cctv.id +
              '" muted playsinline data-stream-url="' +
              (streamUrl || '').replace(/"/g, '&quot;') +
              webrtcAttr +
              '"></video>') +
          '<div class="cctv-pose-layer" aria-hidden="true"></div>' +
          '<div class="cctv-bbox-layer" aria-hidden="true"></div>' +
          '</div>';
      }
    }
  }

  const headerClick =
    enablePoolHeaderClick && pool.id != null && pool.code
      ? 'onclick="openPoolFullscreenPopup(' +
        JSON.stringify(String(pool.id)) +
        ',' +
        JSON.stringify(String(pool.code)) +
        ',event); return false;" style="cursor: pointer;"'
      : '';

  const actionBar = buildPoolActionToolbarHtml(pool, {
    enableActions: Boolean(sectionOptions?.enableRotationButton),
    rotationIntervalSec: rotSec,
  });

  return (
    '<div class="pool-section" data-pool-code="' +
    (poolCodeForRotation ? poolCodeForRotation.replace(/"/g, '&quot;') : '') +
    '" data-pool-id="' +
    (pool.id != null ? String(pool.id) : '') +
    '">' +
    '<div class="pool-header pool-' +
    (pool.status || 'inactive') +
    '" ' +
    headerClick +
    '>' +
    pool.name +
    ' (' +
    pool.code +
    ')' +
    '</div>' +
    actionBar +
    (cellsHtml
      ? '<div class="grid-container" style="grid-template-rows: repeat(' +
        maxRow +
        ', auto); grid-template-columns: repeat(' +
        maxCol +
        ', 1fr);">' +
        cellsHtml +
        '</div>'
      : '<div class="no-cctv">등록된 CCTV가 없음</div>') +
    '</div>'
  );
}

/** 통합 관제 새 창 모니터링 HTML (전체·단일 시설 공통) */
export function buildCentralMonitoringPopupHtml(options: {
  title: string;
  poolsPerRow: number;
  poolGridsInfo: PoolGridEntry[];
  useRemotePlayback: boolean;
  enablePoolHeaderClick?: boolean;
  statusBarExtra?: string;
  rotationIntervalSec?: number;
  enablePoolRotationButton?: boolean;
  showSinglePoolRotationInToolbar?: boolean;
  /** false면 CCTV 셀 클릭 → 순환 보기 비활성 (기본 true) */
  enableCctvClickRotation?: boolean;
  /** false면 iframe 로드 시 브라우저 전체화면 자동 요청 안 함 */
  autoRequestFullscreen?: boolean;
  /** 전체 관제 새 창: 우하단 배경 흰색/검정 토글 */
  enableBackgroundThemeToggle?: boolean;
  /** bbox API 폴링 베이스 (기본 REACT_APP_API_ORIGIN) */
  apiOrigin?: string;
  /** 닫기 버튼 문구 */
  closeButtonLabel?: string;
  /** 시설 단독 로그인 경로에서 site_monitor_prefer_local 적용 */
  applySiteLocal?: boolean;
}): string {
  const {
    title,
    poolsPerRow,
    poolGridsInfo,
    useRemotePlayback,
    enablePoolHeaderClick = true,
    statusBarExtra,
    rotationIntervalSec = 5,
    enablePoolRotationButton = false,
    showSinglePoolRotationInToolbar = false,
    enableCctvClickRotation = true,
    autoRequestFullscreen = true,
    enableBackgroundThemeToggle = false,
    apiOrigin = API_ORIGIN,
    closeButtonLabel,
    applySiteLocal = false,
  } = options;

  const sectionsHtml = poolGridsInfo
    .map((entry) =>
      buildPoolSectionHtml(entry, useRemotePlayback, enablePoolHeaderClick, {
        enableRotationButton: enablePoolRotationButton,
        rotationIntervalSec,
        enableCctvClickRotation,
        applySiteLocal,
      })
    )
    .join('');

  const singlePoolCode =
    showSinglePoolRotationInToolbar && poolGridsInfo.length === 1
      ? poolGridsInfo[0].pool.code
      : undefined;

  const statusLine =
    '마지막 업데이트: ' +
    new Date().toLocaleString() +
    (statusBarExtra ? ' | ' + statusBarExtra : '');

  const autoFsScript = autoRequestFullscreen
    ? 'setTimeout(function() {' +
      '  if (autoFullscreenAttempted) return;' +
      '  autoFullscreenAttempted = true;' +
      '  requestDocFullscreen();' +
      '}, 250);' +
      'document.body.addEventListener("click", function onFirstClick(ev) {' +
      '  if (ev.target && ev.target.closest && ev.target.closest(".popup-toolbar, .popup-btn, .popup-icon-btn, .pool-toolbar, .pool-ai-toggle, .fullscreen-button, .bg-theme-button, .cctv-cell-clickable")) return;' +
      '  requestDocFullscreen();' +
      '  document.body.removeEventListener("click", onFirstClick);' +
      '});'
    : '';

  return (
    '<!DOCTYPE html>' +
    '<html>' +
    '<head>' +
    '<meta charset="UTF-8">' +
    '<meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover">' +
    '<title>' +
    title +
    '</title>' +
    '<style>' +
    'body, html { margin: 0; padding: 0; background-color: #000; color: white; font-family: Arial; overflow-x: hidden; overflow-y: auto; width: 100%; min-height: 100vh; }' +
    '.main-grid { display: grid; grid-template-columns: repeat(' +
    poolsPerRow +
    ', 1fr); gap: 0; padding: 0; padding-bottom: 14px; width: 100%; box-sizing: border-box; background-color: #000; align-items: start; }' +
    '@media (max-width: 900px) { .main-grid { grid-template-columns: repeat(2, 1fr); } }' +
    '@media (max-width: 600px) { .main-grid { grid-template-columns: 1fr; } .pool-header { height: auto; min-height: 28px; line-height: 1.3; white-space: normal; padding: 6px 8px; font-size: 13px; } .fullscreen-button { width: 40px; height: 40px; bottom: 28px; right: 12px; } .status-bar { height: auto; min-height: 18px; line-height: 1.3; padding: 4px 6px; font-size: 11px; } }' +
    '.pool-section { background-color: #000; overflow: hidden; display: flex; flex-direction: column; border: 1px solid #777; min-width: 0; }' +
    '.pool-header { background-color: #222; padding: 2px 8px; font-size: 12px; font-weight: bold; border-left: 3px solid #4caf50; border-bottom: 1px solid #555; height: 14px; line-height: 14px; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; flex-shrink: 0; }' +
    '.pool-header:hover { background-color: #333; }' +
    '.pool-active { border-left-color: #4caf50; }' +
    '.pool-inactive { border-left-color: #f44336; }' +
    '.pool-maintenance { border-left-color: #ff9800; }' +
    '.grid-container { display: grid; gap: 0; padding: 0; margin: 0; background-color: #000; width: 100%; }' +
    '.cctv-cell { position: relative; background-color: #000; overflow: hidden; margin: 0; padding: 0; width: 100%; aspect-ratio: 16/9; }' +
    '.cctv-cell-clickable { cursor: pointer; }' +
    '.cctv-cell-clickable:hover { outline: 1px solid rgba(76, 175, 80, 0.65); outline-offset: -1px; }' +
    MONITORING_POPUP_BBOX_CSS +
    'video { position: absolute; left: 0; top: 0; width: 100%; height: 100%; object-fit: contain; background-color: #000; display: block; }' +
    '.status-bar { position: fixed; bottom: 0; left: 0; right: 0; background: rgba(34,34,34,0.9); color: white; padding: 2px 5px; font-size: 10px; z-index: 100; text-align: center; height: 14px; line-height: 14px; border-top: 1px solid #555; }' +
    '.fullscreen-button { position: fixed; bottom: 20px; right: 10px; width: 24px; height: 24px; background: rgba(0,0,0,0.5); color: white; border: none; border-radius: 50%; cursor: pointer; display: flex; align-items: center; justify-content: center; z-index: 200; box-shadow: 0 1px 3px rgba(0,0,0,0.2); }' +
    '.fullscreen-button:hover { background: rgba(0,0,0,0.8); }' +
    (enableBackgroundThemeToggle ? MONITORING_POPUP_BG_THEME_CSS : '') +
    '.no-cctv { display: flex; align-items: center; justify-content: center; flex: 1; font-size: 12px; color: #888; min-height: 120px; }' +
    POPUP_TOOLBAR_CSS +
    '</style>' +
    '</head>' +
    '<body>' +
    buildPopupToolbarHtml({
      showClose: true,
      showRotation: Boolean(singlePoolCode),
      poolCode: singlePoolCode,
      rotationIntervalSec,
      closeLabel: closeButtonLabel,
    }) +
    '<div class="main-grid">' +
    sectionsHtml +
    '</div>' +
    '<div class="status-bar">' +
    statusLine +
    '</div>' +
    (enableBackgroundThemeToggle
      ? '<button type="button" id="bg-theme-toggle" class="bg-theme-button" title="밝은 배경">' +
        '<svg id="bg-theme-icon-light" width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">' +
        '<path fill="white" d="M12 7c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5zM2 13h2c.55 0 1-.45 1-1s-.45-1-1-1H2c-.55 0-1 .45-1 1s.45 1 1 1zm18 0h2c.55 0 1-.45 1-1s-.45-1-1-1h-2c-.55 0-1 .45-1 1s.45 1 1 1zM11 2v2c0 .55.45 1 1 1s1-.45 1-1V2c0-.55-.45-1-1-1s-1 .45-1 1zm0 18v2c0 .55.45 1 1 1s1-.45 1-1v-2c0-.55-.45-1-1-1s-1 .45-1 1zM5.99 4.58a.996.996 0 0 0-1.41 0 .996.996 0 0 0 0 1.41l1.06 1.06c.39.39 1.03.39 1.41 0s.39-1.03 0-1.41L5.99 4.58zm12.37 12.37a.996.996 0 0 0-1.41 0 .996.996 0 0 0 0 1.41l1.06 1.06c.39.39 1.03.39 1.41 0a.996.996 0 0 0 0-1.41l-1.06-1.06zm1.06-10.96a.996.996 0 0 0 0-1.41.996.996 0 0 0-1.41 0l-1.06 1.06c-.39.39-.39 1.03 0 1.41s1.03.39 1.41 0l1.06-1.06zM7.05 18.36a.996.996 0 0 0 0-1.41.996.996 0 0 0-1.41 0l-1.06 1.06c-.39.39-.39 1.03 0 1.41s1.03.39 1.41 0l1.06-1.06z"/>' +
        '</svg>' +
        '<svg id="bg-theme-icon-dark" width="20" height="20" viewBox="0 0 24 24" style="display:none" aria-hidden="true">' +
        '<path fill="white" d="M12 3a9 9 0 1 0 9 9c0-.46-.04-.92-.1-1.36a5.389 5.389 0 0 1-4.4 2.26 5.403 5.403 0 0 1-3.14-9.8c-.44-.06-.9-.1-1.36-.1z"/>' +
        '</svg>' +
        '</button>'
      : '') +
    '<button id="fullscreen-toggle" class="fullscreen-button" title="전체화면 전환">' +
    '<svg width="24" height="24" viewBox="0 0 24 24">' +
    '<path fill="white" d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />' +
    '</svg>' +
    '</button>' +
    '<script src="https://cdn.jsdelivr.net/npm/hls.js@latest"></script>' +
    '<script>' +
    MONITORING_POPUP_CLOSE_HANDLER_SCRIPT +
    MONITORING_POPUP_ROTATION_OPEN_SCRIPT +
    'document.addEventListener("DOMContentLoaded", function() {' +
    'const videos = {};' +
    'var autoFullscreenAttempted = false;' +
    'function isDocFullscreen() {' +
    '  return !!(document.fullscreenElement || document.webkitFullscreenElement);' +
    '}' +
    'function requestDocFullscreen() {' +
    '  if (isDocFullscreen()) return;' +
    '  var el = document.documentElement;' +
    '  var fn = el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen;' +
    '  if (fn) fn.call(el).catch(function() {});' +
    '}' +
    'function exitDocFullscreen() {' +
    '  var fn = document.exitFullscreen || document.webkitExitFullscreen || document.msExitFullscreen;' +
    '  if (fn) fn.call(document).catch(function(err) { console.error(err); });' +
    '}' +
    autoFsScript +
    'const fullscreenToggle = document.getElementById("fullscreen-toggle");' +
    'if (fullscreenToggle) {' +
    '  fullscreenToggle.addEventListener("click", function(e) {' +
    '    e.stopPropagation();' +
    '    if (!isDocFullscreen()) {' +
    '      requestDocFullscreen();' +
    '    } else {' +
    '      exitDocFullscreen();' +
    '    }' +
    '  });' +
    '}' +
    (enableBackgroundThemeToggle ? MONITORING_POPUP_BG_THEME_SCRIPT : '') +
    'function initializeStream(video, streamUrl, retryCount, whepUrl) {' +
    '  retryCount = retryCount || 0;' +
    '  var maxRetries = 5;' +
    '  whepUrl = whepUrl || video.getAttribute("data-webrtc-url") || "";' +
    '  try {' +
    '    if (whepUrl && retryCount === 0 && window.RTCPeerConnection) {' +
    '      var pc = new RTCPeerConnection({ iceServers: [{ urls: "stun:stun.l.google.com:19302" }] });' +
    '      pc.addTransceiver("video", { direction: "recvonly" });' +
    '      pc.addTransceiver("audio", { direction: "recvonly" });' +
    '      pc.ontrack = function(ev) {' +
    '        video.srcObject = (ev.streams && ev.streams[0]) ? ev.streams[0] : new MediaStream([ev.track]);' +
    '        video.play().catch(function() {});' +
    '      };' +
    '      pc.createOffer().then(function(offer) {' +
    '        return pc.setLocalDescription(offer).then(function() {' +
    '          return new Promise(function(resolve) {' +
    '            if (pc.iceGatheringState === "complete") resolve();' +
    '            else pc.onicegatheringstatechange = function() {' +
    '              if (pc.iceGatheringState === "complete") resolve();' +
    '            };' +
    '            setTimeout(resolve, 2000);' +
    '          });' +
    '        });' +
    '      }).then(function() {' +
    '        return fetch(whepUrl, {' +
    '          method: "POST",' +
    '          headers: { "Content-Type": "application/sdp" },' +
    '          body: pc.localDescription && pc.localDescription.sdp' +
    '        });' +
    '      }).then(function(res) {' +
    '        if (!res.ok) throw new Error("whep " + res.status);' +
    '        return res.text();' +
    '      }).then(function(sdp) {' +
    '        return pc.setRemoteDescription({ type: "answer", sdp: sdp });' +
    '      }).then(function() {' +
    '        videos[video.id] = { video: video, pc: pc };' +
    '      }).catch(function(err) {' +
    '        try { pc.close(); } catch (e) {}' +
    '        console.warn("WHEP fallback HLS", err);' +
    '        initializeStream(video, streamUrl, 1, "");' +
    '      });' +
    '      return;' +
    '    }' +
    '    if (streamUrl && streamUrl.indexOf(".m3u8") >= 0 && typeof Hls !== "undefined" && Hls.isSupported()) {' +
    '      var hls = new Hls({' +
    '        enableWorker: true,' +
    '        lowLatencyMode: true,' +
    '        backBufferLength: 90,' +
    '        maxBufferSize: 30 * 1000 * 1000,' +
    '        maxBufferLength: 30,' +
    '        fragLoadingMaxRetry: 6,' +
    '        manifestLoadingMaxRetry: 6,' +
    '        levelLoadingMaxRetry: 6,' +
    '        fragLoadingRetryDelay: 500,' +
    '        manifestLoadingRetryDelay: 500,' +
    '        levelLoadingRetryDelay: 500' +
    '      });' +
    '      hls.loadSource(streamUrl);' +
    '      hls.attachMedia(video);' +
    '      hls.on(Hls.Events.MANIFEST_PARSED, function() {' +
    '        video.play().catch(function(e) { console.warn(e); });' +
    '      });' +
    '      hls.on(Hls.Events.ERROR, function(event, data) {' +
    '        if (data.fatal) {' +
    '          switch (data.type) {' +
    '            case Hls.ErrorTypes.NETWORK_ERROR: hls.startLoad(); break;' +
    '            case Hls.ErrorTypes.MEDIA_ERROR: hls.recoverMediaError(); break;' +
    '            default:' +
    '              if (retryCount < maxRetries) {' +
    '                setTimeout(function() { initializeStream(video, streamUrl, retryCount + 1, ""); }, 1000);' +
    '              }' +
    '              break;' +
    '          }' +
    '        }' +
    '      });' +
    '      videos[video.id] = { video: video, hls: hls };' +
    '    } else if (streamUrl) {' +
    '      video.src = streamUrl;' +
    '      video.addEventListener("loadedmetadata", function() {' +
    '        video.play().catch(function(e) { console.warn(e); });' +
    '      });' +
    '      video.addEventListener("error", function() {' +
    '        if (retryCount < maxRetries) {' +
    '          setTimeout(function() { initializeStream(video, streamUrl, retryCount + 1, ""); }, 1000);' +
    '        }' +
    '      });' +
    '      videos[video.id] = { video: video };' +
    '    }' +
    '  } catch (err) {' +
    '    if (retryCount < maxRetries) {' +
    '      setTimeout(function() { initializeStream(video, streamUrl, retryCount + 1, ""); }, 1000);' +
    '    }' +
    '  }' +
    '}' +
    'function initializeAllVideos() {' +
    '  document.querySelectorAll("video").forEach(function(video) {' +
    '    var streamUrl = video.getAttribute("data-stream-url");' +
    '    var whepUrl = video.getAttribute("data-webrtc-url");' +
    '    if (streamUrl || whepUrl) initializeStream(video, streamUrl || "", 0, whepUrl || "");' +
    '  });' +
    '}' +
    'function initializeRtspSnapshots() {' +
    '  var localOrigin = ' +
    JSON.stringify(LOCAL_PREVIEW_ORIGIN) +
    ';' +
    '  document.querySelectorAll("img[data-rtsp-snapshot=\\"1\\"]").forEach(function(img) {' +
    '    var streamUrl = img.getAttribute("data-stream-url");' +
    '    if (!streamUrl) return;' +
    '    function loadOnce() {' +
    '      fetch(localOrigin + "/api/stream-preview", {' +
    '        method: "POST",' +
    '        headers: { "Content-Type": "application/json" },' +
    '        body: JSON.stringify({ url: streamUrl, rtsp_url: streamUrl })' +
    '      }).then(function(res) {' +
    '        if (!res.ok) throw new Error("local preview " + res.status);' +
    '        return res.blob();' +
    '      }).then(function(blob) {' +
    '        if (img._snapUrl) URL.revokeObjectURL(img._snapUrl);' +
    '        img._snapUrl = URL.createObjectURL(blob);' +
    '        img.src = img._snapUrl;' +
    '      }).catch(function() {});' +
    '    }' +
    '    loadOnce();' +
    '    setInterval(loadOnce, 2500);' +
    '  });' +
    '}' +
    'setTimeout(function() { initializeAllVideos(); initializeRtspSnapshots(); }, 1000);' +
    'setInterval(function() {' +
    '  Object.keys(videos).forEach(function(key) {' +
    '    var item = videos[key];' +
    '    if (item.hls && item.hls.media && item.hls.media.paused) {' +
    '      item.hls.media.play().catch(function() {});' +
    '    } else if (item.video && item.video.paused) {' +
    '      item.video.play().catch(function() {});' +
    '    }' +
    '  });' +
    '}, 5000);' +
    '});' +
    '</script>' +
    buildMonitoringPopupApiBootstrapScript(apiOrigin) +
    '<script>' +
    MONITORING_POPUP_BBOX_SCRIPT +
    MONITORING_POPUP_POOL_AI_SCRIPT +
    '</script>' +
    '</body>' +
    '</html>'
  );
}

/** CCTV 순환 전체화면 새 창 */
export function buildCctvRotationPopupHtml(options: {
  title: string;
  poolName: string;
  poolId?: number | string;
  slides: { url: string; name: string; location: string; cctvId: number; webrtcUrl?: string }[];
  intervalSec: number;
  initialCctvId?: number;
  startRotationPaused?: boolean;
  apiOrigin?: string;
  closeButtonLabel?: string;
}): string {
  const {
    title,
    poolName,
    poolId,
    slides,
    intervalSec,
    initialCctvId,
    startRotationPaused = false,
    apiOrigin = API_ORIGIN,
    closeButtonLabel,
  } = options;
  const safeInterval = Math.min(300, Math.max(1, Math.floor(intervalSec) || 5));
  const slidesJson = escapeScriptJson(slides);
  const initialCctvIdJson =
    initialCctvId != null && Number.isFinite(Number(initialCctvId))
      ? String(Math.floor(Number(initialCctvId)))
      : 'null';
  const startPausedJson = startRotationPaused ? 'true' : 'false';

  return (
    '<!DOCTYPE html>' +
    '<html><head><meta charset="UTF-8">' +
    '<meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover">' +
    '<title>' +
    title +
    '</title><style>' +
    'body, html { margin: 0; padding: 0; background: #000; color: #fff; font-family: Arial, sans-serif; overflow: hidden; width: 100%; height: 100%; }' +
    '.rot-shell { display: flex; flex-direction: column; width: 100vw; height: 100vh; height: 100dvh; }' +
    '.rot-header { flex-shrink: 0; background: #222; padding: 6px 12px; font-size: 13px; border-bottom: 1px solid #555; }' +
    '.rot-controls { flex-shrink: 0; display: flex; align-items: center; flex-wrap: wrap; gap: 8px; padding: 6px 12px; background: #1a1a1a; border-bottom: 1px solid #444; font-size: 12px; z-index: 20; }' +
    '.rot-controls label { display: flex; align-items: center; gap: 6px; color: #ddd; }' +
    '.rot-controls input[type="number"] { width: 64px; background: #333; color: #fff; border: 1px solid #666; border-radius: 4px; padding: 4px 6px; font-size: 12px; }' +
    '.rot-controls .popup-btn { padding: 4px 10px; font-size: 11px; }' +
    '.rot-stage { flex: 1; position: relative; min-height: 0; background: #000; overflow: hidden; }' +
    '.rot-zoom-viewport { position: absolute; inset: 0; overflow: hidden; touch-action: none; -webkit-user-select: none; user-select: none; }' +
    '.rot-zoom-viewport.is-zoomed { cursor: grab; }' +
    '.rot-zoom-viewport.is-panning { cursor: grabbing; }' +
    '.rot-zoom-content { position: absolute; inset: 0; width: 100%; height: 100%; transform-origin: center center; will-change: transform; }' +
    '#rot-video { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: contain; background: #000; pointer-events: none; }' +
    '.rot-zoom-reset-btn { position: absolute; top: 10px; right: 10px; z-index: 18; display: none; align-items: center; justify-content: center; width: 36px; height: 36px; padding: 0; border-radius: 6px; border: 1px solid #666; background: rgba(34,34,34,0.92); color: #fff; cursor: pointer; }' +
    '.rot-zoom-reset-btn:hover { background: rgba(60,60,60,0.95); }' +
    '.rot-zoom-reset-btn.is-visible { display: flex; }' +
    '.rot-zoom-reset-btn svg { display: block; }' +
    '.rot-nav-btn { position: absolute; top: 50%; transform: translateY(-50%); z-index: 16; width: 44px; height: 72px; padding: 0; border: 1px solid rgba(102,102,102,0.85); border-radius: 6px; background: rgba(20,20,20,0.55); color: #fff; cursor: pointer; display: flex; align-items: center; justify-content: center; }' +
    '.rot-nav-btn:hover { background: rgba(40,40,40,0.82); }' +
    '.rot-nav-btn:active { background: rgba(60,60,60,0.9); }' +
    '.rot-nav-btn:focus-visible { outline: 2px solid #4caf50; outline-offset: 2px; }' +
    '.rot-nav-prev { left: 10px; }' +
    '.rot-nav-next { right: 10px; }' +
    '.rot-label { position: absolute; left: 12px; bottom: 12px; background: rgba(0,0,0,0.72); padding: 8px 12px; border-radius: 4px; z-index: 10; max-width: min(90vw, 480px); }' +
    '.rot-cctv-name { font-size: 18px; font-weight: bold; line-height: 1.3; word-break: break-word; }' +
    '.rot-cctv-loc { font-size: 12px; color: #ccc; margin-top: 4px; line-height: 1.2; }' +
    '.rot-meta { flex-shrink: 0; background: rgba(34,34,34,0.95); padding: 4px 10px; font-size: 11px; text-align: center; border-top: 1px solid #555; }' +
    '.rot-pause-overlay { position: absolute; inset: 0; display: none; align-items: center; justify-content: center; flex-direction: column; gap: 6px; background: rgba(0,0,0,0.45); z-index: 12; pointer-events: none; }' +
    '.rot-pause-overlay.is-visible { display: flex; }' +
    '.rot-pause-msg { font-size: 22px; font-weight: bold; letter-spacing: -0.02em; }' +
    '.rot-pause-hint { font-size: 13px; color: #ccc; }' +
    '.popup-btn-rotation-pause { border-color: #ff9800; }' +
    '.popup-btn-video-freeze { border-color: #42a5f5; }' +
    '@media (max-width: 600px) {' +
    '  .rot-header { padding: 4px 8px; font-size: 12px; }' +
    '  .rot-controls { gap: 6px; padding: 4px 8px; font-size: 11px; }' +
    '  .rot-controls .popup-btn { padding: 6px 10px; font-size: 12px; min-height: 36px; }' +
    '  .rot-nav-btn { width: 40px; height: 56px; }' +
    '  .rot-nav-prev { left: 4px; }' +
    '  .rot-nav-next { right: 4px; }' +
    '  .rot-zoom-reset-btn { top: 8px; right: 8px; width: 44px; height: 44px; }' +
    '  .rot-label { left: 8px; bottom: 8px; padding: 6px 10px; max-width: calc(100vw - 56px); }' +
    '  .rot-cctv-name { font-size: 15px; }' +
    '  .rot-pause-msg { font-size: 18px; }' +
    '  .rot-pause-hint { font-size: 12px; padding: 0 16px; text-align: center; }' +
    '}' +
    POPUP_TOOLBAR_CSS +
    MONITORING_POPUP_BBOX_CSS +
    '</style>' +
    '<script src="https://cdn.jsdelivr.net/npm/hls.js@latest"></script></head><body>' +
    '<div class="rot-shell">' +
    buildPopupToolbarHtml({ showClose: true, closeLabel: closeButtonLabel }) +
    '<div class="rot-header">' +
    poolName +
    ' · CCTV 순환 보기</div>' +
    '<div class="rot-controls">' +
    '<label for="rot-interval-sec">순환(초)' +
    '<input id="rot-interval-sec" type="number" min="1" max="300" step="1" value="' +
    safeInterval +
    '" title="1~300초" />' +
    '</label>' +
    '<button type="button" class="popup-btn" id="rot-interval-apply">적용</button>' +
    '<button type="button" class="popup-btn popup-btn-rotation-pause" id="rot-rotation-pause-toggle">순환 일시정지</button>' +
    '<button type="button" class="popup-btn popup-btn-video-freeze" id="rot-video-freeze-toggle">화면 멈춤</button>' +
    '</div>' +
    '<div class="rot-stage">' +
    '<button type="button" id="rot-nav-prev" class="rot-nav-btn rot-nav-prev" title="이전 CCTV (←)" aria-label="이전 CCTV">' +
    '<svg width="28" height="28" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/></svg></button>' +
    '<button type="button" id="rot-nav-next" class="rot-nav-btn rot-nav-next" title="다음 CCTV (→)" aria-label="다음 CCTV">' +
    '<svg width="28" height="28" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/></svg></button>' +
    '<button type="button" id="rot-zoom-reset" class="rot-zoom-reset-btn" title="원래 크기" aria-label="원래 크기">' +
    '<svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">' +
    '<path fill="currentColor" d="M5 15h3v4H5v-4zm13 0h3v4h-3v-4zM5 5h3v4H5V5zm13 0h3v4h-3V5z"/>' +
    '</svg></button>' +
    '<div id="rot-zoom-viewport" class="rot-zoom-viewport">' +
    '<div id="rot-zoom-content" class="rot-zoom-content">' +
    '<video id="rot-video" muted playsinline></video>' +
    '<div id="rot-pose-layer" class="cctv-pose-layer" aria-hidden="true"></div>' +
    '<div id="rot-bbox-layer" class="cctv-bbox-layer" aria-hidden="true"></div>' +
    '</div></div>' +
    '<div id="rot-video-freeze-overlay" class="rot-pause-overlay" aria-hidden="true">' +
    '<div class="rot-pause-msg">화면 멈춤</div>' +
    '<div class="rot-pause-hint">「화면 재생」을 누르면 영상이 다시 움직</div>' +
    '</div>' +
    '<div class="rot-label">' +
    '<div id="rot-cctv-name" class="rot-cctv-name"></div>' +
    '<div id="rot-cctv-loc" class="rot-cctv-loc"></div>' +
    '</div>' +
    '</div>' +
    '<div id="rot-meta" class="rot-meta"></div></div>' +
    '<script>' +
    MONITORING_POPUP_CLOSE_HANDLER_SCRIPT +
    'document.addEventListener("DOMContentLoaded", function() {' +
    'var slides = ' +
    slidesJson +
    ';' +
    'var poolIdForBbox = ' +
    JSON.stringify(poolId != null ? String(poolId) : '') +
    ';' +
    'var initialCctvId = ' +
    initialCctvIdJson +
    ';' +
    'var startRotationPaused = ' +
    startPausedJson +
    ';' +
    'var ROT_LS_KEY = "monitoringRotationIntervalSec";' +
    'var idx = 0;' +
    'var hls = null;' +
    'var rotTimer = null;' +
    'var rotationPaused = false;' +
    'var videoFrozen = false;' +
    'var intervalMs = ' +
    safeInterval * 1000 +
    ';' +
    'var video = document.getElementById("rot-video");' +
    'var zoomViewport = document.getElementById("rot-zoom-viewport");' +
    'var zoomContent = document.getElementById("rot-zoom-content");' +
    'var zoomResetBtn = document.getElementById("rot-zoom-reset");' +
    'var zoomScale = 1;' +
    'var panX = 0;' +
    'var panY = 0;' +
    'var ZOOM_MIN = 1;' +
    'var ZOOM_MAX = 5;' +
    'var isPanning = false;' +
    'var panDragStartX = 0;' +
    'var panDragStartY = 0;' +
    'var panOriginX = 0;' +
    'var panOriginY = 0;' +
    'var pinchActive = false;' +
    'var pinchStartDist = 0;' +
    'var pinchStartScale = 1;' +
    'var pinchStartPanX = 0;' +
    'var pinchStartPanY = 0;' +
    'var pinchMidX = 0;' +
    'var pinchMidY = 0;' +
    'function applyZoomTransform() {' +
    '  if (!zoomContent) return;' +
    '  zoomContent.style.transform = "translate(" + panX + "px, " + panY + "px) scale(" + zoomScale + ")";' +
    '  if (zoomViewport) {' +
    '    zoomViewport.classList.toggle("is-zoomed", zoomScale > ZOOM_MIN + 0.001);' +
    '  }' +
    '  if (zoomResetBtn) {' +
    '    zoomResetBtn.classList.toggle("is-visible", zoomScale > ZOOM_MIN + 0.001);' +
    '  }' +
    '}' +
    'function clampPan() {' +
    '  if (!zoomViewport || zoomScale <= ZOOM_MIN) { panX = 0; panY = 0; return; }' +
    '  var w = zoomViewport.clientWidth || 0;' +
    '  var h = zoomViewport.clientHeight || 0;' +
    '  var maxPanX = (w * (zoomScale - 1)) / 2;' +
    '  var maxPanY = (h * (zoomScale - 1)) / 2;' +
    '  panX = Math.min(maxPanX, Math.max(-maxPanX, panX));' +
    '  panY = Math.min(maxPanY, Math.max(-maxPanY, panY));' +
    '}' +
    'function resetZoom() {' +
    '  zoomScale = ZOOM_MIN;' +
    '  panX = 0;' +
    '  panY = 0;' +
    '  isPanning = false;' +
    '  pinchActive = false;' +
    '  if (zoomViewport) zoomViewport.classList.remove("is-panning");' +
    '  applyZoomTransform();' +
    '}' +
    'function zoomAtWheel(ev) {' +
    '  if (!zoomViewport || !zoomContent) return;' +
    '  ev.preventDefault();' +
    '  var rect = zoomViewport.getBoundingClientRect();' +
    '  var cx = rect.width / 2;' +
    '  var cy = rect.height / 2;' +
    '  var mx = ev.clientX - rect.left - cx;' +
    '  var my = ev.clientY - rect.top - cy;' +
    '  var oldScale = zoomScale;' +
    '  var factor = ev.deltaY > 0 ? 0.9 : 1.1;' +
    '  var newScale = oldScale * factor;' +
    '  if (newScale < ZOOM_MIN) newScale = ZOOM_MIN;' +
    '  if (newScale > ZOOM_MAX) newScale = ZOOM_MAX;' +
    '  if (Math.abs(newScale - oldScale) < 0.0001) return;' +
    '  if (newScale <= ZOOM_MIN) {' +
    '    resetZoom();' +
    '    return;' +
    '  }' +
    '  panX = mx - ((mx - panX) * newScale) / oldScale;' +
    '  panY = my - ((my - panY) * newScale) / oldScale;' +
    '  zoomScale = newScale;' +
    '  clampPan();' +
    '  applyZoomTransform();' +
    '}' +
    'if (zoomViewport) {' +
    '  zoomViewport.addEventListener("wheel", zoomAtWheel, { passive: false });' +
    '  zoomViewport.addEventListener("mousedown", function(ev) {' +
    '    if (ev.button !== 0 || zoomScale <= ZOOM_MIN) return;' +
    '    isPanning = true;' +
    '    panDragStartX = ev.clientX;' +
    '    panDragStartY = ev.clientY;' +
    '    panOriginX = panX;' +
    '    panOriginY = panY;' +
    '    zoomViewport.classList.add("is-panning");' +
    '    ev.preventDefault();' +
    '  });' +
    '}' +
    'window.addEventListener("mousemove", function(ev) {' +
    '  if (!isPanning || !zoomViewport) return;' +
    '  panX = panOriginX + (ev.clientX - panDragStartX);' +
    '  panY = panOriginY + (ev.clientY - panDragStartY);' +
    '  clampPan();' +
    '  applyZoomTransform();' +
    '});' +
    'window.addEventListener("mouseup", function() {' +
    '  if (!isPanning) return;' +
    '  isPanning = false;' +
    '  if (zoomViewport) zoomViewport.classList.remove("is-panning");' +
    '});' +
    'function touchDistance(t0, t1) {' +
    '  var dx = t0.clientX - t1.clientX;' +
    '  var dy = t0.clientY - t1.clientY;' +
    '  return Math.sqrt(dx * dx + dy * dy);' +
    '}' +
    'function beginOneFingerPan(touch) {' +
    '  if (!zoomViewport || zoomScale <= ZOOM_MIN) return;' +
    '  isPanning = true;' +
    '  panDragStartX = touch.clientX;' +
    '  panDragStartY = touch.clientY;' +
    '  panOriginX = panX;' +
    '  panOriginY = panY;' +
    '  zoomViewport.classList.add("is-panning");' +
    '}' +
    'function applyPinchScale(newScale) {' +
    '  if (newScale < ZOOM_MIN) newScale = ZOOM_MIN;' +
    '  if (newScale > ZOOM_MAX) newScale = ZOOM_MAX;' +
    '  if (newScale <= ZOOM_MIN) { resetZoom(); return; }' +
    '  panX = pinchMidX - ((pinchMidX - pinchStartPanX) * newScale) / pinchStartScale;' +
    '  panY = pinchMidY - ((pinchMidY - pinchStartPanY) * newScale) / pinchStartScale;' +
    '  zoomScale = newScale;' +
    '  clampPan();' +
    '  applyZoomTransform();' +
    '}' +
    'if (zoomViewport) {' +
    '  zoomViewport.addEventListener("touchstart", function(ev) {' +
    '    if (ev.touches.length === 2) {' +
    '      pinchActive = true;' +
    '      isPanning = false;' +
    '      if (zoomViewport) zoomViewport.classList.remove("is-panning");' +
    '      var t0 = ev.touches[0];' +
    '      var t1 = ev.touches[1];' +
    '      pinchStartDist = Math.max(1, touchDistance(t0, t1));' +
    '      pinchStartScale = zoomScale;' +
    '      pinchStartPanX = panX;' +
    '      pinchStartPanY = panY;' +
    '      var rect = zoomViewport.getBoundingClientRect();' +
    '      var cx = rect.width / 2;' +
    '      var cy = rect.height / 2;' +
    '      pinchMidX = (t0.clientX + t1.clientX) / 2 - rect.left - cx;' +
    '      pinchMidY = (t0.clientY + t1.clientY) / 2 - rect.top - cy;' +
    '      ev.preventDefault();' +
    '    } else if (ev.touches.length === 1 && zoomScale > ZOOM_MIN) {' +
    '      pinchActive = false;' +
    '      beginOneFingerPan(ev.touches[0]);' +
    '      ev.preventDefault();' +
    '    }' +
    '  }, { passive: false });' +
    '  zoomViewport.addEventListener("touchmove", function(ev) {' +
    '    if (pinchActive && ev.touches.length >= 2) {' +
    '      ev.preventDefault();' +
    '      var dist = touchDistance(ev.touches[0], ev.touches[1]);' +
    '      applyPinchScale(pinchStartScale * (dist / pinchStartDist));' +
    '      return;' +
    '    }' +
    '    if (isPanning && ev.touches.length === 1) {' +
    '      ev.preventDefault();' +
    '      panX = panOriginX + (ev.touches[0].clientX - panDragStartX);' +
    '      panY = panOriginY + (ev.touches[0].clientY - panDragStartY);' +
    '      clampPan();' +
    '      applyZoomTransform();' +
    '    }' +
    '  }, { passive: false });' +
    '  function endTouchGesture(ev) {' +
    '    if (ev.touches.length < 2) pinchActive = false;' +
    '    if (ev.touches.length === 0) {' +
    '      isPanning = false;' +
    '      if (zoomViewport) zoomViewport.classList.remove("is-panning");' +
    '      return;' +
    '    }' +
    '    if (ev.touches.length === 1 && zoomScale > ZOOM_MIN) {' +
    '      beginOneFingerPan(ev.touches[0]);' +
    '    }' +
    '  }' +
    '  zoomViewport.addEventListener("touchend", endTouchGesture);' +
    '  zoomViewport.addEventListener("touchcancel", endTouchGesture);' +
    '}' +
    'if (zoomResetBtn) {' +
    '  zoomResetBtn.addEventListener("click", function(ev) {' +
    '    ev.stopPropagation(); ev.preventDefault();' +
    '    resetZoom();' +
    '  });' +
    '}' +
    'window.addEventListener("resize", function() { clampPan(); applyZoomTransform(); });' +
    'var videoFreezeOverlay = document.getElementById("rot-video-freeze-overlay");' +
    'var rotationPauseBtn = document.getElementById("rot-rotation-pause-toggle");' +
    'var videoFreezeBtn = document.getElementById("rot-video-freeze-toggle");' +
    'var nameEl = document.getElementById("rot-cctv-name");' +
    'var locEl = document.getElementById("rot-cctv-loc");' +
    'var metaEl = document.getElementById("rot-meta");' +
    'var intervalInput = document.getElementById("rot-interval-sec");' +
    'function clampIntervalSec(n) {' +
    '  var v = Math.floor(Number(n));' +
    '  if (!Number.isFinite(v)) v = 5;' +
    '  return Math.min(300, Math.max(1, v));' +
    '}' +
    'function readIntervalSecFromUi() {' +
    '  return clampIntervalSec(intervalInput && intervalInput.value);' +
    '}' +
    'function applyIntervalSec(sec) {' +
    '  var s = clampIntervalSec(sec);' +
    '  intervalMs = s * 1000;' +
    '  if (intervalInput) intervalInput.value = String(s);' +
    '  try { localStorage.setItem(ROT_LS_KEY, String(s)); } catch (e) {}' +
    '}' +
    'function restartRotationTimer() {' +
    '  if (rotTimer) { clearInterval(rotTimer); rotTimer = null; }' +
    '  if (!slides.length || rotationPaused) return;' +
    '  rotTimer = setInterval(function() { showSlide(idx + 1); }, intervalMs);' +
    '}' +
    'var navPrevBtn = document.getElementById("rot-nav-prev");' +
    'var navNextBtn = document.getElementById("rot-nav-next");' +
    'function updateNavButtons() {' +
    '  var multi = slides.length > 1;' +
    '  if (navPrevBtn) navPrevBtn.style.display = multi ? "flex" : "none";' +
    '  if (navNextBtn) navNextBtn.style.display = multi ? "flex" : "none";' +
    '}' +
    'function goToAdjacentSlide(delta) {' +
    '  if (!slides.length || slides.length < 2) return;' +
    '  showSlide(idx + delta);' +
    '  setRotationPaused(true);' +
    '}' +
    'function isTypingTarget(el) {' +
    '  if (!el || !el.tagName) return false;' +
    '  var tag = String(el.tagName).toUpperCase();' +
    '  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;' +
    '  if (el.isContentEditable) return true;' +
    '  return false;' +
    '}' +
    'document.addEventListener("keydown", function(ev) {' +
    '  if (isTypingTarget(document.activeElement)) return;' +
    '  if (ev.key === " " || ev.key === "Spacebar") {' +
    '    ev.preventDefault();' +
    '    setRotationPaused(!rotationPaused);' +
    '    return;' +
    '  }' +
    '  if (ev.key !== "ArrowLeft" && ev.key !== "ArrowRight") return;' +
    '  if (!slides.length || slides.length < 2) return;' +
    '  ev.preventDefault();' +
    '  if (ev.key === "ArrowLeft") goToAdjacentSlide(-1);' +
    '  else goToAdjacentSlide(1);' +
    '});' +
    'if (navPrevBtn) {' +
    '  navPrevBtn.addEventListener("click", function(ev) {' +
    '    ev.stopPropagation(); ev.preventDefault();' +
    '    goToAdjacentSlide(-1);' +
    '  });' +
    '}' +
    'if (navNextBtn) {' +
    '  navNextBtn.addEventListener("click", function(ev) {' +
    '    ev.stopPropagation(); ev.preventDefault();' +
    '    goToAdjacentSlide(1);' +
    '  });' +
    '}' +
    'function updateMetaLine() {' +
    '  if (!slides.length || !metaEl) return;' +
    '  var s = slides[idx];' +
    '  var line = (idx + 1) + " / " + slides.length + " · " + (s.name || "") + " · " + Math.round(intervalMs / 1000) + "초 간격";' +
    '  if (rotationPaused) line += " · 순환 일시정지";' +
    '  if (videoFrozen) line += " · 화면 멈춤";' +
    '  metaEl.textContent = line;' +
    '}' +
    'function setRotationPaused(paused) {' +
    '  rotationPaused = paused;' +
    '  if (rotationPauseBtn) rotationPauseBtn.textContent = paused ? "순환 재개" : "순환 일시정지";' +
    '  if (paused) {' +
    '    if (rotTimer) { clearInterval(rotTimer); rotTimer = null; }' +
    '  } else {' +
    '    restartRotationTimer();' +
    '  }' +
    '  updateMetaLine();' +
    '}' +
    'function setVideoFrozen(frozen) {' +
    '  videoFrozen = frozen;' +
    '  if (videoFreezeOverlay) {' +
    '    videoFreezeOverlay.classList.toggle("is-visible", frozen);' +
    '    videoFreezeOverlay.setAttribute("aria-hidden", frozen ? "false" : "true");' +
    '  }' +
    '  if (videoFreezeBtn) videoFreezeBtn.textContent = frozen ? "화면 재생" : "화면 멈춤";' +
    '  if (frozen) {' +
    '    try { video.pause(); } catch (e) {}' +
    '  } else {' +
    '    try { video.play().catch(function() {}); } catch (e) {}' +
    '  }' +
    '  updateMetaLine();' +
    '}' +
    'function onIntervalApply(ev) {' +
    '  if (ev) { ev.stopPropagation(); ev.preventDefault(); }' +
    '  applyIntervalSec(readIntervalSecFromUi());' +
    '  restartRotationTimer();' +
    '  updateMetaLine();' +
    '}' +
    'var applyBtn = document.getElementById("rot-interval-apply");' +
    'if (applyBtn) applyBtn.addEventListener("click", onIntervalApply);' +
    'if (intervalInput) {' +
    '  intervalInput.addEventListener("keydown", function(e) {' +
    '    if (e.key === "Enter") onIntervalApply(e);' +
    '  });' +
    '}' +
    'if (rotationPauseBtn) {' +
    '  rotationPauseBtn.addEventListener("click", function(ev) {' +
    '    ev.stopPropagation(); ev.preventDefault();' +
    '    setRotationPaused(!rotationPaused);' +
    '  });' +
    '}' +
    'if (videoFreezeBtn) {' +
    '  videoFreezeBtn.addEventListener("click", function(ev) {' +
    '    ev.stopPropagation(); ev.preventDefault();' +
    '    setVideoFrozen(!videoFrozen);' +
    '  });' +
    '}' +
    'function requestDocFullscreen() {' +
    '  if (document.fullscreenElement) return;' +
    '  var el = document.documentElement;' +
    '  var fn = el.requestFullscreen || el.webkitRequestFullscreen;' +
    '  if (fn) fn.call(el).catch(function() {});' +
    '}' +
    'setTimeout(requestDocFullscreen, 300);' +
    'function destroyHls() { if (hls) { hls.destroy(); hls = null; } }' +
    'var rotPc = null;' +
    'function destroyWebrtc() { if (rotPc) { try { rotPc.close(); } catch (e) {} rotPc = null; } }' +
    'function playHlsOrSrc(streamUrl) {' +
    '  destroyHls();' +
    '  video.removeAttribute("src");' +
    '  video.srcObject = null;' +
    '  try { video.load(); } catch (e) {}' +
    '  if (!streamUrl) return;' +
    '  var lower = String(streamUrl).toLowerCase();' +
    '  if (lower.indexOf("rtsp://") === 0 || lower.indexOf("rtsps://") === 0) {' +
    '    var localOrigin = ' +
    JSON.stringify(LOCAL_PREVIEW_ORIGIN) +
    ';' +
    '    if (video._rtspTimer) { clearInterval(video._rtspTimer); video._rtspTimer = null; }' +
    '    function loadRtspSnap() {' +
    '      fetch(localOrigin + "/api/stream-preview", {' +
    '        method: "POST",' +
    '        headers: { "Content-Type": "application/json" },' +
    '        body: JSON.stringify({ url: streamUrl, rtsp_url: streamUrl })' +
    '      }).then(function(res) {' +
    '        if (!res.ok) throw new Error("local preview");' +
    '        return res.blob();' +
    '      }).then(function(blob) {' +
    '        if (video._snapUrl) URL.revokeObjectURL(video._snapUrl);' +
    '        video._snapUrl = URL.createObjectURL(blob);' +
    '        video.poster = video._snapUrl;' +
    '      }).catch(function() {});' +
    '    }' +
    '    loadRtspSnap();' +
    '    video._rtspTimer = setInterval(loadRtspSnap, 2500);' +
    '    return;' +
    '  }' +
    '  if (streamUrl.indexOf(".m3u8") >= 0 && typeof Hls !== "undefined" && Hls.isSupported()) {' +
    '    hls = new Hls({ enableWorker: true, lowLatencyMode: true, maxBufferLength: 20 });' +
    '    hls.loadSource(streamUrl);' +
    '    hls.attachMedia(video);' +
    '    hls.on(Hls.Events.MANIFEST_PARSED, function() { if (!videoFrozen) video.play().catch(function() {}); });' +
    '  } else {' +
    '    video.src = streamUrl;' +
    '    if (!videoFrozen) video.play().catch(function() {});' +
    '  }' +
    '}' +
    'function playUrl(streamUrl, whepUrl) {' +
    '  destroyWebrtc();' +
    '  destroyHls();' +
    '  video.removeAttribute("src");' +
    '  video.srcObject = null;' +
    '  try { video.load(); } catch (e) {}' +
    '  whepUrl = whepUrl || "";' +
    '  if (whepUrl && window.RTCPeerConnection) {' +
    '    rotPc = new RTCPeerConnection({ iceServers: [{ urls: "stun:stun.l.google.com:19302" }] });' +
    '    rotPc.addTransceiver("video", { direction: "recvonly" });' +
    '    rotPc.addTransceiver("audio", { direction: "recvonly" });' +
    '    rotPc.ontrack = function(ev) {' +
    '      video.srcObject = (ev.streams && ev.streams[0]) ? ev.streams[0] : new MediaStream([ev.track]);' +
    '      if (!videoFrozen) video.play().catch(function() {});' +
    '    };' +
    '    rotPc.createOffer().then(function(offer) {' +
    '      return rotPc.setLocalDescription(offer).then(function() {' +
    '        return new Promise(function(resolve) {' +
    '          if (rotPc.iceGatheringState === "complete") resolve();' +
    '          else rotPc.onicegatheringstatechange = function() {' +
    '            if (rotPc.iceGatheringState === "complete") resolve();' +
    '          };' +
    '          setTimeout(resolve, 2000);' +
    '        });' +
    '      });' +
    '    }).then(function() {' +
    '      return fetch(whepUrl, { method: "POST", headers: { "Content-Type": "application/sdp" }, body: rotPc.localDescription.sdp });' +
    '    }).then(function(res) {' +
    '      if (!res.ok) throw new Error("whep");' +
    '      return res.text();' +
    '    }).then(function(sdp) {' +
    '      return rotPc.setRemoteDescription({ type: "answer", sdp: sdp });' +
    '    }).catch(function() {' +
    '      destroyWebrtc();' +
    '      playHlsOrSrc(streamUrl);' +
    '    });' +
    '    return;' +
    '  }' +
    '  playHlsOrSrc(streamUrl);' +
    '}' +
    'function showSlide(i) {' +
    '  if (!slides.length) return;' +
    '  resetZoom();' +
    '  idx = ((i % slides.length) + slides.length) % slides.length;' +
    '  var s = slides[idx];' +
    '  nameEl.textContent = s.name || "";' +
    '  if (s.location && s.location !== s.name) {' +
    '    locEl.textContent = "위치: " + s.location;' +
    '    locEl.style.display = "block";' +
    '  } else {' +
    '    locEl.textContent = "";' +
    '    locEl.style.display = "none";' +
    '  }' +
    '  updateMetaLine();' +
    '  playUrl(s.url, s.webrtcUrl || "");' +
    '  if (window.__popupBboxSetRotationTarget && poolIdForBbox && s.cctvId != null) {' +
    '    window.__popupBboxSetRotationTarget(poolIdForBbox, s.cctvId);' +
    '  }' +
    '  if (videoFrozen) { try { video.pause(); } catch (e) {} }' +
    '  updateNavButtons();' +
    '}' +
    'if (!slides.length) {' +
    '  metaEl.textContent = "재생할 CCTV가 없음";' +
    '  if (nameEl) nameEl.textContent = "";' +
    '} else {' +
    '  try {' +
    '    var stored = parseInt(localStorage.getItem(ROT_LS_KEY) || "", 10);' +
    '    if (Number.isFinite(stored)) applyIntervalSec(stored);' +
    '  } catch (e) {}' +
    '  var startIdx = 0;' +
    '  if (initialCctvId != null) {' +
    '    for (var si = 0; si < slides.length; si++) {' +
    '      if (Number(slides[si].cctvId) === Number(initialCctvId)) { startIdx = si; break; }' +
    '    }' +
    '  }' +
    '  showSlide(startIdx);' +
    '  if (startRotationPaused) {' +
    '    setRotationPaused(true);' +
    '  } else {' +
    '    restartRotationTimer();' +
    '  }' +
    '}' +
    'try {' +
    '  document.body.setAttribute("tabindex", "-1");' +
    '  document.body.focus({ preventScroll: true });' +
    '} catch (eFocus) {}' +
    '});' +
    '</script>' +
    buildMonitoringPopupApiBootstrapScript(apiOrigin) +
    '<script>' +
    MONITORING_POPUP_BBOX_SCRIPT +
    '</script></body></html>'
  );
}
